// src/utils.ts
import { consts as consts2, schemas } from "@guildxyz/types";
import { keccak256 } from "ethers";
import randomBytes from "randombytes";

// src/common.ts
import { consts } from "@guildxyz/types";
var globals = {
  apiBaseUrl: process.env.GUILD_SDK_BASE_URL ?? "https://api.guild.xyz/v2",
  headers: {
    "Content-Type": "application/json",
    [consts.SDK_VERSION_HEADER_NAME]: "2.0.0-rc.2",
    [consts.SDK_PROJECT_NAME_HEADER_NAME]: ""
  }
};
var setProjectName = (projectName) => {
  globals.headers[consts.SDK_PROJECT_NAME_HEADER_NAME] = projectName;
};

// src/error.ts
var GuildAPICallFailed = class extends Error {
  constructor(endpoint, message, statusCode, correlationId) {
    super(
      `Guild API call failed on ${endpoint} with http code ${statusCode} with message: ${message ?? "Unexpected Error"}
ID: ${correlationId}`
    );
    this.endpoint = endpoint;
    this.message = message;
    this.statusCode = statusCode;
    this.correlationId = correlationId;
  }
};
var GuildSDKValidationError = class extends Error {
  constructor(zodError) {
    super(`A value passed as request body did not pass validation.`);
    this.zodError = zodError;
  }
};

// src/utils.ts
var recreateMessage = (params) => `${params.msg}

Address: ${params.addr}
Method: ${params.method}${params.method === consts2.AuthMethod.EIP1271 ? `
ChainId: ${params.chainId}` : ""}${params.hash ? `
Hash: ${params.hash}` : ""}
Nonce: ${params.nonce}
Timestamp: ${params.ts}`;
var createSigner = {
  fromEthersWallet: (wallet, { msg = "Please sign this message" } = {}) => async (payload = {}, getMessage = recreateMessage) => {
    const stringPayload = JSON.stringify(payload);
    const params = schemas.AuthenticationParamsSchema.parse({
      method: consts2.AuthMethod.EOA,
      addr: wallet.address,
      msg,
      nonce: randomBytes(32).toString("base64"),
      ts: `${Date.now()}`,
      hash: keccak256(Buffer.from(stringPayload))
    });
    if (params.method !== consts2.AuthMethod.EOA) {
      throw new Error("This shouldn't happen, please open an issue");
    }
    const sig = await wallet.signMessage(Buffer.from(getMessage(params)));
    return { params, sig, payload: stringPayload };
  },
  custom: (sign, address, {
    msg = "Please sign this message",
    chainIdOfSmartContractWallet
  } = {}) => async (payload = {}, getMessage = recreateMessage) => {
    const stringPayload = JSON.stringify(payload);
    const params = schemas.AuthenticationParamsSchema.parse({
      addr: address,
      msg,
      nonce: randomBytes(32).toString("base64"),
      ts: `${Date.now()}`,
      hash: keccak256(Buffer.from(stringPayload)),
      ...typeof chainIdOfSmartContractWallet === "number" ? {
        chainId: chainIdOfSmartContractWallet.toString(),
        method: consts2.AuthMethod.EIP1271
      } : { method: consts2.AuthMethod.EOA }
    });
    const sig = await sign(getMessage(params));
    return { params, sig, payload: stringPayload };
  }
};
var callGuildAPI = async (params) => {
  let parsedQueryParams = null;
  if (params.queryParams) {
    if (params.queryParamsSchema) {
      const validationResult = schemas[params.queryParamsSchema].safeParse(
        params.queryParams
      );
      if (validationResult.success) {
        parsedQueryParams = params.queryParams;
      } else {
        throw new GuildSDKValidationError(validationResult.error);
      }
    } else {
      parsedQueryParams = params.queryParams;
    }
  }
  const queryParamEntries = Object.entries(parsedQueryParams ?? {}).filter(([, value]) => !!value).map(([key, value]) => [key, `${value}`]);
  const baseUrl = params.url.startsWith("/v1/") ? globals.apiBaseUrl.replace("/v2", "") : globals.apiBaseUrl;
  const url = queryParamEntries.length > 0 ? `${baseUrl}${params.url}?${new URLSearchParams(queryParamEntries)}` : `${baseUrl}${params.url}`;
  let parsedPayload = {};
  if (params.method !== "GET" && !!params.body?.schema && !!params.body?.data) {
    const validationResult = schemas[params.body.schema].safeParse(
      params.body.data
    );
    if (validationResult.success) {
      parsedPayload = validationResult.data;
    } else {
      throw new GuildSDKValidationError(validationResult.error);
    }
  }
  const authentication = await params.signer?.(parsedPayload);
  const isPrivileged = "headers" in (authentication ?? {});
  const response = await fetch(url, {
    method: params.method,
    body: (
      // eslint-disable-next-line no-nested-ternary
      params.method === "GET" ? void 0 : isPrivileged ? JSON.stringify(parsedPayload) : JSON.stringify(authentication ?? parsedPayload)
    ),
    headers: {
      ...params.method === "GET" && authentication && !isPrivileged ? {
        [consts2.PARAMS_HEADER_NAME]: Buffer.from(
          JSON.stringify(authentication.params)
        ).toString("base64"),
        [consts2.SIG_HEADER_NAME]: Buffer.from(
          authentication.sig.startsWith("0x") ? authentication.sig.slice(2) : authentication.sig,
          "hex"
        ).toString("base64")
      } : {},
      ...globals.headers,
      ...isPrivileged ? authentication.headers : {}
    }
  });
  const responseBody = await response.json();
  if (!response.ok) {
    throw new GuildAPICallFailed(
      url.replace(baseUrl, ""),
      responseBody?.errors?.[0]?.msg ?? responseBody?.message ?? "Unexpected Error",
      response.status,
      response.headers.get("x-correlation-id") ?? ""
    );
  }
  return responseBody;
};

// src/clients/guildReward.ts
var guildReward = {
  get: (guildIdOrUrlName, guildPlatformId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/guild-platforms/${guildPlatformId}`,
    method: "GET",
    signer
  }),
  getAll: (guildIdOrUrlName, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/guild-platforms`,
    method: "GET",
    signer
  }),
  create: (guildIdOrUrlName, guildPlatformCreationParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/guild-platforms`,
    method: "POST",
    body: {
      schema: "GuildRewardCreationSchema",
      data: guildPlatformCreationParams
    },
    signer
  }),
  update: (guildIdOrUrlName, guildPlatformId, guildPlatformUpdateParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/guild-platforms/${guildPlatformId}`,
    method: "PUT",
    body: {
      schema: "GuildRewardUpdateSchema",
      data: guildPlatformUpdateParams
    },
    signer
  }),
  delete: (guildIdOrUrlName, guildPlatformId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/guild-platforms/${guildPlatformId}`,
    method: "DELETE",
    signer
  })
};
var guildReward_default = guildReward;

// src/clients/requirement.ts
var requirement = {
  get: (guildIdOrUrlName, roleId, requirementId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/requirements/${requirementId}`,
    method: "GET",
    signer
  }),
  getAll: (guildIdOrUrlName, roleId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/requirements`,
    method: "GET",
    signer
  }),
  create: (guildIdOrUrlName, roleId, requirementCreationParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/requirements`,
    method: "POST",
    body: {
      data: requirementCreationParams,
      schema: "RequirementCreationPayloadSchema"
    },
    signer
  }),
  update: (guildIdOrUrlName, roleId, requirementId, requirementUpdateParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/requirements/${requirementId}`,
    method: "PUT",
    body: {
      data: requirementUpdateParams,
      schema: "RequirementUpdatePayloadSchema"
    },
    signer
  }),
  delete: (guildIdOrUrlName, roleId, requirementId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/requirements/${requirementId}`,
    method: "DELETE",
    signer
  })
};
var requirement_default = requirement;

// src/clients/rolePlatform.ts
var rolePlatform = {
  get: (guildIdOrUrlName, roleId, rolePlatformId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms/${rolePlatformId}`,
    method: "GET",
    signer
  }),
  getAll: (guildIdOrUrlName, roleId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms`,
    method: "GET",
    signer
  }),
  claim: (guildIdOrUrlName, roleId, rolePlatformId, args, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms/${rolePlatformId}/claim`,
    method: "POST",
    body: {
      data: { args },
      schema: "RolePlatformClaimPayloadSchema"
    },
    signer
  }),
  create: (guildIdOrUrlName, roleId, rolePlatformCreationParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms`,
    method: "POST",
    body: {
      data: rolePlatformCreationParams,
      schema: "RoleRewardCreationPayloadSchema"
    },
    signer
  }),
  update: (guildIdOrUrlName, roleId, rolePlatformId, rolePlatformUpdateParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms/${rolePlatformId}`,
    method: "PUT",
    body: {
      data: rolePlatformUpdateParams,
      schema: "RoleRewardUpdatePayloadSchema"
    },
    signer
  }),
  delete: (guildIdOrUrlName, roleId, rolePlatformId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}/role-platforms/${rolePlatformId}`,
    method: "DELETE",
    signer
  })
};
var rolePlatform_default = rolePlatform;

// src/clients/role.ts
var role = {
  requirement: requirement_default,
  reward: rolePlatform_default,
  get: (guildIdOrUrlName, roleId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}`,
    method: "GET",
    signer
  }),
  getAll: (guildIdOrUrlName, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles`,
    method: "GET",
    signer
  }),
  create: (guildIdOrUrlName, roleCreationParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles`,
    method: "POST",
    body: {
      data: roleCreationParams,
      schema: "RoleCreationPayloadSchema"
    },
    signer
  }),
  update: (guildIdOrUrlName, roleId, roleUpdateParams, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}`,
    method: "PUT",
    body: {
      data: roleUpdateParams,
      schema: "RoleUpdatePayloadSchema"
    },
    signer
  }),
  delete: (guildIdOrUrlName, roleId, signer) => callGuildAPI({
    url: `/guilds/${guildIdOrUrlName}/roles/${roleId}`,
    method: "DELETE",
    signer
  })
};
var role_default = role;

// src/clients/guild.ts
var guild = {
  role: role_default,
  reward: guildReward_default,
  get: (guildIdOrUrlName) => callGuildAPI({ url: `/guilds/${guildIdOrUrlName}`, method: "GET" }),
  getMany: (guildIds) => callGuildAPI({
    url: `/guilds`,
    method: "GET",
    queryParams: {
      guildIds: guildIds.join(",")
    },
    queryParamsSchema: "GuildGetManyQueryParamsSchema"
  }),
  search: (params) => callGuildAPI({
    url: `/guilds`,
    method: "GET",
    queryParams: params,
    queryParamsSchema: "GuildSearchQueryParamsSchema"
  }),
  getMembers: (guildId, signer) => callGuildAPI({
    url: `/guilds/${guildId}/members`,
    method: "GET",
    signer
  }),
  getUserMemberships: (guildId, userId, signer) => callGuildAPI({
    url: `/guilds/${guildId}/members/${userId}`,
    method: "GET",
    signer
  }),
  create: (guildCreationParams, signer) => callGuildAPI({
    url: `/guilds`,
    method: "POST",
    signer,
    body: {
      data: guildCreationParams,
      schema: "GuildCreationPayloadSchema"
    }
  }),
  update: (guildId, guildUpdateParams, signer) => callGuildAPI({
    url: `/guilds/${guildId}`,
    method: "PUT",
    body: {
      data: guildUpdateParams,
      schema: "GuildUpdatePayloadSchema"
    },
    signer
  }),
  delete: (guildId, signer) => callGuildAPI({
    url: `/guilds/${guildId}`,
    method: "DELETE",
    signer
  }),
  join: (guildId, signer) => callGuildAPI({
    url: `/v1/user/join`,
    method: "POST",
    body: {
      schema: "JoinActionPayloadSchema",
      data: {
        guildId
      }
    },
    signer
  }),
  // join: (
  //   guildId: number,
  //   signer: SignerFunction,
  //   pollOptions?: PollOptions<JoinJob>
  // ) =>
  //   createAndAwaitJob<JoinJob>(
  //     "/actions/join",
  //     {
  //       schema: "JoinActionPayloadSchema",
  //       data: { guildId },
  //     },
  //     { guildId },
  //     signer,
  //     pollOptions
  //   ),
  accessCheck: (guildId, signer) => callGuildAPI({
    url: `/v1/guild/access/${guildId}/0x0000000000000000000000000000000000000000`,
    method: "GET",
    signer
  })
  // accessCheck: (
  //   guildId: number,
  //   signer: SignerFunction,
  //   pollOptions?: PollOptions<AccessCheckJob>
  // ) =>
  //   createAndAwaitJob<AccessCheckJob>(
  //     "/actions/access-check",
  //     {
  //       schema: "JoinActionPayloadSchema",
  //       data: { guildId },
  //     },
  //     { guildId },
  //     signer,
  //     pollOptions
  //   ),
  // statusUpdate: async (
  //   guildId: number,
  //   signer: SignerFunction,
  //   pollOptions?: PollOptions<StatusUpdateJob>
  // ) => {
  //   const roles = await guild.role.getAll(guildId, signer);
  //   return createAndAwaitJob<StatusUpdateJob>(
  //     "/actions/status-update",
  //     {
  //       schema: "StatusUpdateActionPayloadSchema",
  //       data: {
  //         roleIds: (roles ?? []).map(({ id }) => id),
  //       },
  //     },
  //     { guildId },
  //     signer,
  //     pollOptions
  //   );
  // },
};
var guild_default = guild;

// src/clients/platform.ts
var platform = {
  getGuildByPlatform: (platformName, platformGuildId, signer) => callGuildAPI({
    url: `/platforms/${platformName}/guilds/${platformGuildId}`,
    method: "GET",
    signer
  }),
  getUserGuildAccessByPlatform: (platformName, platformGuildId, platformUserId, signer) => callGuildAPI({
    url: `/platforms/${platformName}/guilds/${platformGuildId}/users/${platformUserId}/access`,
    method: "GET",
    signer
  }),
  withPlatformName: (platformName) => ({
    getGuildByPlatform: (platformGuildId, signer) => platform.getGuildByPlatform(platformName, platformGuildId, signer),
    getUserGuildAccessByPlatform: (platformGuildId, platformUserId, signer) => platform.getUserGuildAccessByPlatform(
      platformName,
      platformGuildId,
      platformUserId,
      signer
    ),
    getUserByPlatformUserId: (platformUserId, signer) => callGuildAPI({
      url: `/platforms/${platformName}/users/${platformUserId}`,
      method: "GET",
      signer
    })
  })
};
var platform_default = platform;

// src/clients/platformUser.ts
var platformUser = {
  get: (userIdOrAddress, platformId, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/platform-users/${platformId}`,
    method: "GET",
    signer
  }),
  getAll: (userIdOrAddress, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/platform-users`,
    method: "GET",
    signer
  }),
  create: (userIdOrAddress, platformUserCreationParams, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/platform-users`,
    method: "POST",
    body: {
      schema: "PlatformUserCreationSchema",
      data: platformUserCreationParams
    },
    signer
  }),
  delete: (userIdOrAddress, platformId, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/platform-users/${platformId}`,
    method: "DELETE",
    signer
  })
};
var platformUser_default = platformUser;

// src/clients/userAddress.ts
var userAddress = {
  get: (userIdOrAddress, address, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/addresses/${address}`,
    method: "GET",
    signer
  }),
  getAll: (userIdOrAddress, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/addresses`,
    method: "GET",
    signer
  }),
  create: async (userIdOrAddress, signerOfNewAddress, signer) => {
    const { params, sig } = await signerOfNewAddress(
      {},
      (p) => `Address: ${p.addr}
Nonce: ${p.nonce}
 Timestamp: ${p.ts}`
    );
    return callGuildAPI({
      url: `/users/${userIdOrAddress}/addresses`,
      method: "POST",
      body: {
        schema: "UserAddressCreationPayloadSchema",
        data: {
          address: params.addr,
          signature: sig,
          nonce: params.nonce,
          timestamp: +params.ts
        }
      },
      signer
    });
  },
  update: (userIdOrAddress, address, addressUpdateParams, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/addresses/${address}`,
    method: "PUT",
    body: {
      schema: "UserAddressUpdatePayloadSchema",
      data: addressUpdateParams
    },
    signer
  }),
  delete: (userIdOrAddress, address, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/addresses/${address}`,
    method: "DELETE",
    signer
  })
};
var userAddress_default = userAddress;

// src/clients/user.ts
var user = {
  platform: platformUser_default,
  address: userAddress_default,
  get: (userIdOrAddress) => callGuildAPI({
    url: `/users/${userIdOrAddress}`,
    method: "GET"
  }),
  getProfile: (userIdOrAddress, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/profile`,
    method: "GET",
    signer: typeof signer === "string" ? void 0 : signer
  }),
  getMemberships: (userIdOrAddress, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/memberships`,
    method: "GET",
    signer
  }),
  delete: (userIdOrAddress, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}`,
    method: "DELETE",
    signer
  }),
  listGateables: (userIdOrAddress, platformName, signer) => callGuildAPI({
    url: `/users/${userIdOrAddress}/platforms/${platformName}/gateables`,
    method: "GET",
    signer
  })
};
var user_default = user;

// src/client.ts
var createGuildClient = (projectName) => {
  if (typeof projectName !== "string" || projectName.length <= 0)
    throw Error("Project name should be a non-empty string");
  setProjectName(projectName);
  return { guild: guild_default, platform: platform_default, user: user_default };
};
var client_default = createGuildClient;
export {
  GuildAPICallFailed,
  GuildSDKValidationError,
  client_default as createGuildClient,
  createSigner
};
